import React, { useState, useEffect } from 'react';
import {
	Modal,
	ModalHeader,
	ModalBody,
	ModalFooter,
	Button,
	Table,
	Spinner,
	Alert,
	Badge,
	Input,
	FormGroup,
	Label,
} from 'reactstrap';
import axios from 'axios';
import { BASE_URL } from '../constants/apiConfig';
import { toast } from 'react-toastify';

const Round2AssignmentModal = ({ isOpen, toggle, onAssignmentComplete }) => {
	const [loading, setLoading] = useState(false);
	const [applications, setApplications] = useState([]);
	const [filteredApplications, setFilteredApplications] = useState([]);
	const [selectedApplications, setSelectedApplications] = useState([]);
	const [assigning, setAssigning] = useState(false);
	const [error, setError] = useState('');
	const [searchQuery, setSearchQuery] = useState('');

	useEffect(() => {
		if (isOpen) {
			fetchEligibleApplications();
			setSearchQuery('');
		}
	}, [isOpen]);

	// Filter applications based on search query
	useEffect(() => {
		if (!searchQuery.trim()) {
			setFilteredApplications(applications);
		} else {
			const query = searchQuery.toLowerCase();
			const filtered = applications.filter((app) => {
				const appId = String(app.application_id || '').toLowerCase();
				const name = String(app.applicant_name || '').toLowerCase();
				const email = String(app.applicant_email || '').toLowerCase();
				
				return appId.includes(query) || name.includes(query) || email.includes(query);
			});
			setFilteredApplications(filtered);
		}
	}, [searchQuery, applications]);

	const fetchEligibleApplications = async () => {
		try {
			setLoading(true);
			setError('');
			const token = sessionStorage.getItem('token');
			
			const response = await axios.get(
				`${BASE_URL}/api/admin/dashboard/round2-eligible-applications`,
				{
					headers: {
						Authorization: `Bearer ${token}`,
					},
				}
			);

			if (response.data.success) {
				setApplications(response.data.data || []);
			} else {
				setError('Failed to fetch eligible applications');
			}
		} catch (err) {
			console.error('Error fetching eligible applications:', err);
			setError(err.response?.data?.message || 'Failed to load applications');
		} finally {
			setLoading(false);
		}
	};

	const handleSelectAll = (e) => {
		if (e.target.checked) {
			setSelectedApplications(filteredApplications.map((app) => app.application_id));
		} else {
			setSelectedApplications([]);
		}
	};

	const handleSelectApplication = (applicationId) => {
		setSelectedApplications((prev) => {
			if (prev.includes(applicationId)) {
				return prev.filter((id) => id !== applicationId);
			} else {
				return [...prev, applicationId];
			}
		});
	};

	const handleAssignRound2 = async () => {
		if (selectedApplications.length === 0) {
			toast.warning('Please select at least one application');
			return;
		}

		try {
			setAssigning(true);
			setError('');
			const token = sessionStorage.getItem('token');

			const response = await axios.post(
				`${BASE_URL}/api/admin/dashboard/assign-round2`,
				{ applicationIds: selectedApplications },
				{
					headers: {
						Authorization: `Bearer ${token}`,
						'Content-Type': 'application/json',
					},
				}
			);

			if (response.data.success) {
				const { assigned, ineligible } = response.data.data;
				
				if (assigned > 0) {
					toast.success(`Successfully assigned ${assigned} application(s) to Round 2 reviewers`);
				}
				
				if (ineligible && ineligible.length > 0) {
					toast.warning(`${ineligible.length} application(s) were ineligible and skipped`);
				}

				// Reset selections and refresh
				setSelectedApplications([]);
				fetchEligibleApplications();
				
				// Notify parent component to refresh
				if (onAssignmentComplete) {
					onAssignmentComplete();
				}
			} else {
				setError(response.data.message || 'Failed to assign reviewers');
			}
		} catch (err) {
			console.error('Error assigning Round 2 reviewers:', err);
			setError(err.response?.data?.message || 'Failed to assign reviewers');
			toast.error('Failed to assign Round 2 reviewers');
		} finally {
			setAssigning(false);
		}
	};

	const formatDate = (dateString) => {
		if (!dateString) return '-';
		return new Date(dateString).toLocaleDateString('en-US', {
			year: 'numeric',
			month: 'short',
			day: 'numeric',
		});
	};

	return (
		<Modal isOpen={isOpen} toggle={toggle} size="xl">
			<ModalHeader toggle={toggle}>
				Assign Round 2 Reviewers
			</ModalHeader>
			<ModalBody>
				{error && (
					<Alert color="danger" className="mb-3">
						{error}
					</Alert>
				)}

				{loading ? (
					<div className="text-center p-5">
						<Spinner color="primary" />
						<p className="mt-3">Loading eligible applications...</p>
					</div>
				) : applications.length === 0 ? (
					<Alert color="info">
						No applications are currently eligible for Round 2 assignment.
						Applications must complete Round 1 review before they can be assigned to Round 2.
					</Alert>
				) : (
					<>
						<div className="mb-3">
							<p className="text-muted mb-2">
								<strong>{applications.length}</strong> application(s) eligible for Round 2 assignment.
								{filteredApplications.length !== applications.length && (
									<span> (Showing {filteredApplications.length} filtered result{filteredApplications.length !== 1 ? 's' : ''})</span>
								)}
							</p>
							<div className="d-flex justify-content-between align-items-center mb-3">
								<FormGroup check className="mb-0">
									<Label check>
										<Input
											type="checkbox"
											checked={selectedApplications.length === filteredApplications.length && filteredApplications.length > 0}
											onChange={handleSelectAll}
										/>
										{' '}Select All {filteredApplications.length !== applications.length ? '(Filtered)' : ''}
									</Label>
								</FormGroup>
								<FormGroup className="mb-0" style={{ width: '300px' }}>
									<Input
										type="text"
										placeholder="Search by Application ID or Name..."
										value={searchQuery}
										onChange={(e) => setSearchQuery(e.target.value)}
									/>
								</FormGroup>
							</div>
							<p className="text-muted small mb-0">
								Select applications below and click "Assign Round 2 Reviewers" to automatically assign 2 reviewers per application.
							</p>
						</div>

						<div style={{ maxHeight: '500px', overflowY: 'auto' }}>
							<Table hover bordered responsive>
								<thead style={{ position: 'sticky', top: 0, backgroundColor: 'white', zIndex: 1 }}>
									<tr>
										<th style={{ width: '50px' }}>Select</th>
										<th>Application ID</th>
										<th>Applicant Name</th>
										<th>Email</th>
										<th>Round 1 Reviewer</th>
										<th>Round 1 Score</th>
										<th>Completed Date</th>
									</tr>
								</thead>
								<tbody>
									{filteredApplications.length === 0 ? (
										<tr>
											<td colSpan="7" className="text-center text-muted">
												No applications match your search criteria
											</td>
										</tr>
									) : (
										filteredApplications.map((app) => (
										<tr key={app.application_id}>
											<td className="text-center">
												<Input
													type="checkbox"
													checked={selectedApplications.includes(app.application_id)}
													onChange={() => handleSelectApplication(app.application_id)}
												/>
											</td>
											<td>
												<Badge color="secondary">{app.application_id}</Badge>
											</td>
											<td>{app.applicant_name || '-'}</td>
											<td>{app.applicant_email || '-'}</td>
											<td>{app.round1_reviewer || '-'}</td>
											<td className="text-center">
												{app.round1_score !== null ? (
													<Badge color="primary">{app.round1_score}</Badge>
												) : (
													'-'
												)}
											</td>
											<td>{formatDate(app.round1_completed_date)}</td>
										</tr>
										))
									)}
								</tbody>
							</Table>
						</div>

						<Alert color="info" className="mt-3 mb-0">
							<strong>Note:</strong> The system will automatically assign 2 reviewers to each selected application 
							using a round-robin strategy. The Round 1 reviewer will be excluded from Round 2 assignments.
						</Alert>
					</>
				)}
			</ModalBody>
			<ModalFooter>
				<div className="d-flex justify-content-between w-100 align-items-center">
					<div>
						{selectedApplications.length > 0 && (
							<span className="text-muted">
								{selectedApplications.length} application(s) selected
							</span>
						)}
					</div>
					<div>
						<Button color="secondary" onClick={toggle} disabled={assigning}>
							Cancel
						</Button>
						{' '}
						<Button
							color="primary"
							onClick={handleAssignRound2}
							disabled={assigning || selectedApplications.length === 0 || loading}
						>
							{assigning ? (
								<>
									<Spinner size="sm" className="me-2" />
									Assigning...
								</>
							) : (
								'Assign Round 2 Reviewers'
							)}
						</Button>
					</div>
				</div>
			</ModalFooter>
		</Modal>
	);
};

export default Round2AssignmentModal;
